/**
 * Shelves menu — click-to-expand behaviour for the mobile drawer.
 *
 * The drawer's own <details> machinery slides submenus in as full panels and traps
 * focus inside them, so the shelf uses a plain button + animated height instead.
 */
class ShelvesMenuCollapsible extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      open: 'is-open',
      animating: 'is-animating',
    };

    this.toggle = this.querySelector('.shelves-menu-collapsible__toggle');
    this.content = this.querySelector('.shelves-menu-collapsible__content');

    if (!this.toggle || !this.content) return;

    this.animation = null;
    this.toggle.addEventListener('click', this.onToggleClick.bind(this));
  }

  get isOpen() {
    return this.classList.contains(this.classes.open);
  }

  onToggleClick() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    // Measured before unhiding so an interrupted close animation resumes from where it is.
    const startHeight = this.content.hidden ? 0 : this.content.offsetHeight;

    this.content.hidden = false;
    this.classList.add(this.classes.open);
    this.toggle.setAttribute('aria-expanded', 'true');
    this.animate(startHeight, this.content.scrollHeight);
  }

  close() {
    const startHeight = this.content.offsetHeight;

    this.classList.remove(this.classes.open);
    this.toggle.setAttribute('aria-expanded', 'false');
    this.animate(startHeight, 0, () => {
      this.content.hidden = true;
    });
  }

  animate(from, to, onFinish) {
    if (this.animation) this.animation.cancel();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches || typeof this.content.animate !== 'function') {
      if (onFinish) onFinish();
      return;
    }

    this.classList.add(this.classes.animating);
    this.animation = this.content.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: 300, easing: 'ease-out' }
    );

    const cleanup = () => {
      this.classList.remove(this.classes.animating);
      this.animation = null;
    };

    this.animation.onfinish = () => {
      cleanup();
      if (onFinish) onFinish();
    };
    this.animation.oncancel = cleanup;
  }
}
customElements.define('shelves-menu-collapsible', ShelvesMenuCollapsible);
